import { Routing } from '../Routes';
import { Redirect, match, useLocation, useParams } from 'react-router-dom';
import { Main } from '../Main/Main';
import { useApi, Res, fetchResourceMap, isRedirect } from '../../state/api';
import { useTranslation } from '../../i18n/i18n';
import { ApiFeatureSheet, ApiProduct, ApiProductPage, ApiUser } from '../../types';
import { Helmet } from 'react-helmet-async';
import { useJsonLD } from '../product/JsonLD';
import { LoaderPage } from './LoaderPage';
import { RedirectStatus } from '../RedirectStatus';
import { SimilarProducts } from '../product/SimilarProducts';
import { HorizontalProductListing } from '../HorizontalProductListing/HorizontalProductListing';
import { Breadcrumbs } from '../Breadcrumbs/Breadcrumbs';
import { ProductPageTopPane } from '../../components/product/ProductPageTopPane';
import { ProductPageBottomPane } from '../../components/product/ProductPageBottomPane';
import { variantListingProducts } from '../product/variants';
import { CategoryTreeContainer } from '../../state/categories';
import { FeatureSheet } from '../../components/product/FeatureSheet';
import { ProductContainer } from '../../state/product';
import { useEffect, useState } from 'react';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useConfig } from '../../config';
import { CampaignProducts } from '../../components/product/CampaignProducts';
import { ViewedProducts } from '../../components/product/ViewedProducts';
import { useViewProduct } from '../../hooks/useViewProduct';
import { usePrevious } from '../../hooks/usePrevious';
import axios from 'axios';
import { useCriteo } from '../../hooks/useCriteo';
import { useSession } from '../../state/session';
import { NotFoundPage } from './NotFoundPage';
import { useRedirectOnError } from '../../hooks/useRedirectOnError';
import { useLoop54Events } from '../../hooks/useLoop54Events';
import { MetaContainer } from '../../state/meta';
import { ComplementaryProducts } from '../product/ComplementaryProducts';
import { TorkFacebookTracking } from '../TorkFaceboookTracking/TorkFacebookTracking';

export const ProductPage = (): JSX.Element => {
	const { perma } = useParams<UrlParams>();
	const { api, domain, store, locale } = useConfig();
	const absolutePath = `https://${domain}/${perma}`;
	const { data: resp, error } = useApi(Res.product, { perma });
	const data = isRedirect(resp) ? undefined : resp;
	const t = useTranslation().product;
	const jsonLD = useJsonLD(data);
	const categories = CategoryTreeContainer.useContainer();
	const products = ProductContainer.useContainer();
	const { sendEvent, sendTorkEvent } = useAnalytics();
	const { sendViewProduct } = useViewProduct();
	const config = useConfig();
	const prevProductId = usePrevious(data?.id);
	const [rerender, setRerender] = useState(0);
	const { sendCriteoEvent } = useCriteo();
	const { user, specialsProducts } = useSession();
	const { routes } = useTranslation();
	const { sendLoop54Event } = useLoop54Events();
	const { cookiebotLoaded } = MetaContainer.useContainer();
	const { marketing } = MetaContainer.useContainer().trackingConsents;
	interface LocationState extends Location {
		state: {
			cameFromSearchResults?: boolean;
		};
	}
	const location = useLocation() as LocationState;

	useEffect(() => {
		if (data?.id) {
			if (prevProductId === data.id) return;
			sendEvent({
				event: 'view_item',
				ecommerce: {
					currency: config.currencyCode,
					value: data.price,
					items: [
						{
							item_id: String(data.id),
							item_name: data.name,
							item_brand: data.brand.name,
							price: data.price,
						},
					],
				},
			});

			if (store === 'kgno' && data.brand?.name?.toLowerCase() === 'tork') {
				sendTorkEvent('PageView');
			}

			sendViewProduct(data.id);
			sendCriteoEvent({ event: 'viewItem', item: data.id });

			if (typeof window.fbq !== 'undefined') {
				if (store === 'kgno') {
					if (data.brand?.name?.toLowerCase() === 'tork' && marketing) {
						window.fbq('track', 'ViewContent');
					}
				} else {
					window.fbq('track', 'ViewContent', {
						content_type: 'product',
						content_ids: [data.id + ''],
						content_name: data.brand?.name ?? '',
						value: 0,
						currency: config.currencyCode,
					});
				}
			}
		}
	}, [data]);

	// Always fetch fresh data in client
	// FIXME: Extract into hook or something
	useEffect(() => {
		if (data) {
			const productIds = [data.id];
			productIds.push(...data.groupProducts.map((g) => g.id));
			productIds.push(...data.accessories.map((g) => g.id));

			// Sort to maximaze cache hits
			productIds.sort();

			axios
				.get(api + Res.refreshedProductData.uri({ productIds }))
				.then(({ data: refreshedData }) => {
					const keysToUpdate = [
						'campaignPrice',
						'quantityInStock',
						'stocked',
						'deliveryNotice',
						'deliveryClass',
					];

					const newData = refreshedData[data.id];
					keysToUpdate.forEach((key) => {
						(data as any)[key] = newData[key];
					});

					// Update groupProducts
					data.groupProducts.forEach((groupProduct) => {
						const groupProductData = refreshedData[groupProduct.id];
						keysToUpdate.forEach((key) => {
							(groupProduct as any)[key] = groupProductData[key];
						});
					});

					// Update accessories
					data.accessories.forEach((accessory) => {
						const accessoryData = refreshedData[accessory.id];
						keysToUpdate.forEach((key) => {
							(accessory as any)[key] = accessoryData[key];
						});
					});

					setRerender(rerender + 1); // force re-render
				})
				.catch((error) => {
					console.error('Error when fetching data:', error);
				});
		}
	}, [data]);

	useEffect(() => {
		if (!!data) products.setCategoryPerma(data.categoryPerma);
	}, [data]);

	useEffect(() => {
		if (cookiebotLoaded) {
			if (data) {
				sendLoop54Event({ events: [{ type: 'click', entity: { type: 'Product', id: data.id + '' } }] });
			}
		}
	}, [data, cookiebotLoaded]);

	const { redirect, notFound } = useRedirectOnError(error);

	if (redirect) return <RedirectStatus to={redirect.newUrl} status={redirect.code} />;
	if (notFound) return <NotFoundPage />;

	if (shouldRedirectToSpecials(user, data, specialsProducts)) return <Redirect to={routes.specialPrices} />;
	if (isRedirect(resp)) return <RedirectStatus to={resp.newUrl} status={resp.code} />;
	if (!data) return <LoaderPage />;

	const { metaDescription, id, groupProducts, accessories, relatedProducts, categoryPerma, image } = data;
	let { title } = data;
	if (store === 'kgdk') {
		title += ' | Ondio.dk (tidligere Worky)';
	} else if (store === 'kgno') {
		title += ' | Ondio.no (tidligere Kontorgiganten)';
	}
	const supercategoryId = categories.getSupercategoryId(categoryPerma ?? '');
	const variantAlternatives = variantListingProducts(data.variants, groupProducts, id, locale);
	const featureSheet: ApiFeatureSheet = data?.featureSheet ? JSON.parse(JSON.stringify(data.featureSheet)) : false;

	return (
		<Main>
			<Helmet>
				<title>{title}</title>
				<meta name="description" content={metaDescription} />
				<link rel="canonical" href={absolutePath} />
				<script type="application/ld+json">{jsonLD}</script>
				(image && image!="" && <meta property="og:image" content={image} />)
			</Helmet>
			<Breadcrumbs perma={data.categoryPerma} />
			<TorkFacebookTracking brand={data.brand.name} />
			<div className="product-page">
				<div className="product-page-pane-wrapper">
					<ProductPageTopPane
						productPageData={data}
						supercategoryId={supercategoryId}
						cameFromSearchResults={location.state?.cameFromSearchResults}
					/>
					<ProductPageBottomPane productPageData={data} featureSheet={featureSheet} />
				</div>
				{featureSheet && featureSheet.featureGroups.length > 1 ? <FeatureSheet data={featureSheet} /> : null}
				<TitledHorizontalProductListing title={t.accessories} products={accessories} />
				{/* a group with variant data gets the selectors in the top pane instead of this list, which then
				    holds only what the selectors are no way to: the members of a sparse group no chain of
				    single-dimension steps reaches, and any member with no variant values at all */}
				{variantAlternatives.length > 0 && (
					<TitledHorizontalProductListing
						title={t.productAlternatives}
						products={variantAlternatives}
						highlightId={id}
						supercategoryId={supercategoryId}
					/>
				)}
				<TitledHorizontalProductListing title={t.weSuggest} products={relatedProducts} />
				<ViewedProducts />
				<SimilarProducts productId={data.id} />
				<ComplementaryProducts productId={data.id} />
				<CampaignProducts />
			</div>
		</Main>
	);
};

function shouldRedirectToSpecials(
	user: ApiUser | undefined,
	data: ApiProductPage | undefined,
	specialsProducts: ApiProduct[]
): boolean {
	if (user?.specialsOnly && data) {
		const hasSpecialPrice = !!specialsProducts.find((e) => e.id === data.id);
		if (!hasSpecialPrice) return true;
	}
	return false;
}

const TitledHorizontalProductListing = ({
	title,
	products,
	highlightId,
	supercategoryId,
}: {
	title: string;
	products: ApiProduct[];
	highlightId?: number;
	supercategoryId?: number;
}) => {
	if (products.length === 0) return <></>;
	return (
		<div className="horizontal-product-listing">
			<h2>{title}</h2>
			<HorizontalProductListing products={products} highlightId={highlightId} supercategoryId={supercategoryId} />
		</div>
	);
};

type UrlParams = { perma: string };

export const productRouting: Routing<UrlParams> = {
	path(_urls) {
		return '/:perma(.*-p-.*)';
	},
	component: ProductPage,
	requiresAuth: false,
	// the same as the api allows for products/by-perma, so the page never outlives its data
	cacheMaxAge: 5000,
	preload: async (config, routeMatch: match<UrlParams>, _query: string) => {
		const { perma } = routeMatch.params;
		return fetchResourceMap(config, Res.product, { perma });
	},
};
